
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, Upload } from "lucide-react";
import { Resource } from "../hooks/useResourceManagement";

interface ResourceListProps {
  resources: Resource[];
  isLoading?: boolean;
  selectedResourceId?: string;
  onAddResource: () => void;
  onEditResource: (resource: Resource) => void;
  onDeleteResource: (resourceId: string) => void;
  onSelectResource?: (resource: Resource) => void;
  showMinimalView?: boolean;
  onDragStart?: (resource: Resource) => void;
  isDraggable?: boolean;
  onImportSuccess?: () => void;
  isActionDisabled?: boolean;
}

const ResourceList = ({
  resources,
  isLoading = false,
  selectedResourceId,
  onAddResource,
  onEditResource,
  onDeleteResource,
  onSelectResource,
  showMinimalView = false,
  onDragStart,
  isDraggable = false,
  onImportSuccess,
  isActionDisabled = false
}: ResourceListProps) => {
  // Format resource ID to show as R0001, R0002, etc.
  const formatResourceId = (id: number) => {
    return `R${String(id).padStart(4, '0')}`;
  };

  // Handle edit icon click - automatically select the resource
  const handleEditClick = (e: React.MouseEvent, resource: Resource) => {
    e.stopPropagation();
    if (onSelectResource) {
      onSelectResource(resource);
    }
    onEditResource(resource);
  };

  // Handle delete icon click - automatically select the resource
  const handleDeleteClick = (e: React.MouseEvent, resource: Resource) => {
    e.stopPropagation();
    if (onSelectResource) {
      onSelectResource(resource);
    }
    onDeleteResource(resource.id);
  };

  // Check if edit/delete buttons should be enabled for this resource
  const isResourceActionEnabled = (resourceId: string) => {
    if (isActionDisabled) return false;
    return !selectedResourceId || selectedResourceId === resourceId;
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="h-full overflow-hidden w-full">
        <div className="text-center py-6 text-gray-400 bg-secondary/5 rounded-md border-2 border-dashed border-primary/30 w-full h-[370px] flex flex-col items-center justify-center">
          <p>Loading resources...</p>
        </div>
      </div>
    );
  }

  // Show empty state when no resources exist
  if (resources.length === 0) {
    return (
      <div className="h-full overflow-hidden w-full">
        <div className="text-center py-6 text-gray-400 bg-secondary/5 rounded-md border-2 border-dashed border-primary/30 w-full h-[370px] flex flex-col items-center justify-center">
          <Upload className="h-12 w-12 text-gray-400 mb-2" />
          <p className="mb-2">No resources found for this project</p>
          <p className="text-xs text-gray-500">Add resources manually or use the Import button to upload from Excel</p>
        </div>
      </div>
    );
  }

  // Show resources table when data exists
  return (
    <div className="h-full overflow-hidden w-full">
      <div className="h-full overflow-y-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-secondary z-10">
            <TableRow className="border-b border-primary/20">
              <TableHead className="text-[10px] text-primary-light">ResourceID</TableHead>
              <TableHead className="text-[10px] text-primary-light">Name</TableHead>
              <TableHead className="text-[10px] text-primary-light">Email</TableHead>
              <TableHead className="text-[10px] w-[8%] text-primary-light">Role Type</TableHead>
              <TableHead className="text-[10px] text-primary-light">Role</TableHead>
              <TableHead className="text-[10px] w-[15%] text-primary-light">Skillset</TableHead>
              <TableHead className="text-[10px] text-primary-light">Hrs/Day</TableHead>
              <TableHead className="text-[10px] text-primary-light">Rate/Hr</TableHead>
              <TableHead className="text-[10px] text-primary-light">Availability</TableHead>
              <TableHead className="text-[10px] text-primary-light">Exp (Yrs)</TableHead>
              <TableHead className="text-[10px] text-primary-light">Past Projects</TableHead>
              <TableHead className="text-right text-[12px] text-primary-light">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {resources.sort((a, b) => a.resource_id - b.resource_id).map((resource) => (
              <TableRow
                key={resource.id}
                className={`border-b border-primary/10 hover:bg-secondary/20 transition-colors ${
                  selectedResourceId === resource.id ? 'bg-secondary/30' : ''
                }`}
                draggable={isDraggable}
                onDragStart={isDraggable ? (e) => {
                  e.dataTransfer.setData('resourceId', resource.id);
                  if (onDragStart) onDragStart(resource);
                } : undefined}
                onClick={() => onSelectResource && onSelectResource(resource)}
              >
                <TableCell className="py-0 text-white">
                  <span className="text-[10px] text-gray-400">{formatResourceId(resource.resource_id)}</span>
                </TableCell>
                <TableCell className="py-0 text-white">
                  <span className="text-[10px]">{resource.name}</span>
                </TableCell>
                <TableCell className="py-0 text-white">
                  <span className="text-[10px]">{resource.email || '-'}</span>
                </TableCell>
                <TableCell className="py-0 text-white">
                  <span className="text-[10px] w-[8%]">{resource.role_type || '-'}</span>
                </TableCell>
                <TableCell className="py-0 text-white">
                  <span className="text-[10px]">{resource.role}</span>
                </TableCell>
                <TableCell className="py-0 text-white">
                  <span className="text-[10px] w-[15%]">{resource.skillset || '-'}</span>
                </TableCell>
                <TableCell className="py-0 text-white">
                  <span className="text-[10px]">{resource.capacity || '-'}</span>
                </TableCell>
                <TableCell className="py-0 text-white">
                  <span className="text-[10px]">{resource.rate !== null && resource.rate !== undefined ? `$${resource.rate}` : '-'}</span>
                </TableCell>
                <TableCell className="py-0 text-white">
                  <span className="text-[10px]">{resource.availability || '-'}</span>
                </TableCell>
                <TableCell className="py-0 text-white">
                  <span className="text-[10px]">{resource.years_experience || '-'}</span>
                </TableCell>
                <TableCell className="py-0 text-white">
                  <span className="text-[10px]">{resource.past_projects || '-'}</span>
                </TableCell>
                <TableCell className="text-right py-0">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleEditClick(e, resource)}
                      className="h-8 w-8 p-0"
                      disabled={!isResourceActionEnabled(resource.id)}
                    >
                      <Edit className={`h-4 w-4 ${isResourceActionEnabled(resource.id) ? 'text-primary' : 'text-gray-500'}`} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={(e) => handleDeleteClick(e, resource)}
                      disabled={!isResourceActionEnabled(resource.id)}
                    >
                      <Trash2 className={`h-4 w-4 ${isResourceActionEnabled(resource.id) ? 'text-red-500' : 'text-gray-500'}`} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ResourceList;
